/**
 * DriftDetailDrawer — the full detail of one drift (D25, mockup 02).
 *
 * `DriftDetailContent` is the body: it fetches the drift (DSC or continuous
 * watch) and renders Carbon's three-column row detail —
 *   What changed     the baseline value beside the drifted value, and how
 *                    Guardian saw it (the provenance line)
 *   Guardian's read  Guardian's recommendation, the risk, and the decision:
 *                    revert in the admin center, accept as the new baseline,
 *                    or reject
 *   History          the audit trail as a Carbon timeline
 * Both drift tables render it inside their in-place expanded rows
 * (`layout="row"`, components/carbon/RowDetail).
 *
 * The default export keeps its path and props: a Carbon side panel
 * (components/carbon/SidePanel) holding the same content stacked in one
 * column, for the places that open a drift from outside the table (the top
 * items above the queue).
 */

import { useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, tcmApi } from '@/lib/api'
import apiClient from '@/lib/api'
import type { TcmDriftDetailWithSource } from '@/lib/api'
import { formatTcmResourceType } from '@/lib/tcmResourceNames'
import { auditEventLabel, buildDscTimeline, buildTcmTimeline, friendlyActor } from '@/lib/audit-timeline'
import {
  provenanceLabel,
  rejectConsequenceCopy,
  deriveWatchAcceptImpact,
  isDeletionDetectorRow,
  deletionDetectorCopy,
  isAcknowledgeRow,
  acknowledgeCopy,
  isUpgradeIntroducedRow,
  upgradeIntroducedCopy,
  triageSuggestionCopy,
  actorLineCopy,
  type DriftPipelineSource,
  type DriftTriageFields,
} from '@/lib/drift-decision-copy'
import { DriftStatus } from '@/types'
import type {
  TcmDriftAuditEntry,
  TcmDriftApprovalImpact,
} from '@/types/tcm'
import { SevPill, StatusPill } from '@/components/Drift/DriftTableWithBulkActions'
import BulkActionCommentModal from '@/components/Drift/BulkActionCommentModal'
import TcmCommentModal from '@/components/Tcm/TcmCommentModal'
import { Button, ButtonLink } from '@/components/carbon/Button'
import { DiffList } from '@/components/carbon/DiffList'
import { IconExternal } from '@/components/carbon/icons'
import { InlineNotification } from '@/components/carbon/InlineNotification'
import { IuvoMark } from '@/components/carbon/IuvoMark'
import { ButtonSet, RowDetail, RowDetailSection } from '@/components/carbon/RowDetail'
import { SeverityGlyph } from '@/components/carbon/SeverityGlyph'
import { SidePanel } from '@/components/carbon/SidePanel'
import { Skeleton } from '@/components/carbon/Skeleton'
import { Tag } from '@/components/carbon/Tag'
import { Timeline, type TimelineItem } from '@/components/carbon/Timeline'
import type { SeverityLevel } from '@/components/carbon/types'
import { GuardiansRead, ValueView } from '@/components/BaselineCompliance/drift-carbon'
import { DriftValueDiff } from '@/components/Drift/DriftValueDiff'
import {
  adminCenterFor,
  formatDriftTimestamp,
  isLongValue,
} from '@/components/BaselineCompliance/drift-carbon-helpers'

interface DriftDetailDrawerProps {
  driftId: string | null
  isOpen: boolean
  onClose: () => void
  onApprove: (id: string, comment?: string) => void
  onReject: (id: string, comment?: string) => void
  isUpdating: boolean
  /**
   * Wave 7 (TCM migration): which collector produced this row.
   * Defaults to 'DSC' so every existing call-site keeps working
   * byte-for-byte. For 'TCM' the drawer fetches via tcmApi and
   * branches the audit-history endpoint + the source-specific
   * field rendering.
   */
  source?: 'DSC' | 'TCM'
  /**
   * Required when `source === 'TCM'` — TCM endpoints are tenant-
   * scoped on the route (`/tcm/drifts/{tenantId}/{driftId}/…`) so the
   * drawer can't look it up from the drift id alone. Ignored for DSC.
   */
  tenantId?: string
  /**
   * True when the signed-in user's role is below Approver (`canChangeDrifts`).
   * Hides Accept / Reject and says who decides instead. The server rejects
   * these writes for a Viewer anyway; this keeps them from clicking into a 403.
   */
  readOnly?: boolean
  /**
   * What accepting this continuous-watch drift does to the size of Continuous
   * watch (lib/watch-budget), shown beside Accept. The queue composes it,
   * because Apply checks the whole staged batch and only the queue knows
   * what's waiting to apply. Omitted or null: no line.
   */
  watchLine?: string | null
}

export interface DriftDetailContentProps {
  driftId: string
  onApprove: (id: string, comment?: string) => void
  onReject: (id: string, comment?: string) => void
  isUpdating: boolean
  source?: 'DSC' | 'TCM'
  tenantId?: string
  readOnly?: boolean
  /** See `DriftDetailDrawerProps.watchLine`. */
  watchLine?: string | null
  /** row: three columns inside an expanded table row. panel: one column (the side panel). */
  layout?: 'row' | 'panel'
}

// Audit entry shape mirrors api.getDriftAuditHistory's return element. Keeping
// it local because the API helper returns an inline structural type (no
// exported alias) — when that gets promoted, swap this for the shared one.
type AuditEntry = Awaited<ReturnType<typeof api.getDriftAuditHistory>>[number]

// Approval-impact response is fetched via raw apiClient (no shared type). The
// fields used in the detail are listed here so the impact block stays readable
// without `any`s scattered through the markup.
interface ImpactedDrift {
  id: string
  setting: string
  severity: import('@/types').DriftSeverity
}
interface ApprovalImpact {
  willTriggerResourceLevelUpdate?: boolean
  reason?: string
  recommendation?: string
  impactedDrifts?: ImpactedDrift[]
}

const REMEDIATION_LABEL: Record<string, string> = {
  ApprovedBaseline: 'Approved baseline',
  RejectedManualFix: 'Manual fix',
  AutoCorrected: 'Auto-corrected',
  TenantSelfCorrected: 'Tenant self-corrected',
  BaselineChangedExternally: 'Baseline changed externally',
}

/* ────────────────────────────────────────────────────────────────────
   Risk score → severity shape. Lines up with the four severity levels
   in the queue so the score reads the same way everywhere.
   ──────────────────────────────────────────────────────────────────── */

function riskLevel(score: number): SeverityLevel {
  if (score >= 0.8) return 'crit'
  if (score >= 0.6) return 'high'
  if (score >= 0.4) return 'med'
  return 'low'
}

/** The accessible name of the decision buttons says a note dialog follows. */
const NOTE_SUFFIX = <span className="sr-only">, add a note</span>

/* ────────────────────────────────────────────────────────────────────
   The drawer (side panel) — the default export
   ──────────────────────────────────────────────────────────────────── */

export default function DriftDetailDrawer({
  driftId,
  isOpen,
  onClose,
  onApprove,
  onReject,
  isUpdating,
  source = 'DSC',
  tenantId,
  readOnly = false,
  watchLine = null,
}: DriftDetailDrawerProps) {
  return (
    <SidePanel open={isOpen && !!driftId} onClose={onClose} size="lg" title="Drift detail" subtitle="Assessed by Guardian">
      {driftId ? (
        <DriftDetailContent
          driftId={driftId}
          onApprove={onApprove}
          onReject={onReject}
          isUpdating={isUpdating}
          source={source}
          tenantId={tenantId}
          readOnly={readOnly}
          watchLine={watchLine}
          layout="panel"
        />
      ) : null}
    </SidePanel>
  )
}

/* ────────────────────────────────────────────────────────────────────
   The detail body — shared by the expanded table rows and the drawer
   ──────────────────────────────────────────────────────────────────── */

export function DriftDetailContent({
  driftId,
  onApprove,
  onReject,
  isUpdating,
  source = 'DSC',
  tenantId,
  readOnly = false,
  watchLine = null,
  layout = 'row',
}: DriftDetailContentProps) {
  const [showCommentModal, setShowCommentModal] = useState(false)
  const [actionType, setActionType] = useState<'approve' | 'reject' | null>(null)
  const [showImpactWarning, setShowImpactWarning] = useState(false)
  const isTcm = source === 'TCM'

  // DSC drift fetch — unchanged shape for backwards compatibility.
  const {
    data: drift,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['configuration-drift', driftId],
    queryFn: () => (driftId ? api.getConfigurationDrift(driftId) : null),
    enabled: !!driftId && !isTcm,
  })

  // TCM drift fetch — tenant-scoped route. The summary list already
  // carried most fields, but we re-fetch the detail to pick up
  // `driftedProperties[]`, `opaPackage`, and `policyIds` for the body.
  const {
    data: tcmDrift,
    isLoading: tcmDriftLoading,
    error: tcmDriftError,
  } = useQuery<TcmDriftDetailWithSource | null>({
    queryKey: ['tcm-drift-detail', tenantId, driftId],
    queryFn: () =>
      driftId && tenantId ? tcmApi.getDrift(tenantId, driftId) : Promise.resolve(null),
    enabled: !!driftId && isTcm && !!tenantId,
  })

  // Check approval impact — DSC source. This endpoint returns the DSC
  // resource-level cascade (sibling drifts that ride the same baseline
  // write). The continuous-watch (TCM) source has its own approval-impact
  // query below with a different, cascade-free shape.
  const { data: approvalImpact } = useQuery({
    queryKey: ['approval-impact', driftId],
    queryFn: async () => {
      if (!driftId) return null
      try {
        const response = await apiClient.get(`/configurationdrift/${driftId}/approval-impact`)
        return response.data
      } catch (error) {
        console.error('Failed to check approval impact:', error)
        return null
      }
    },
    enabled: !!driftId && !isTcm && drift?.status === 'New',
  })

  // Audit history — branched per source. TCM uses the dedicated
  // tenant-scoped endpoint added in Wave 5 (`/tcm/drifts/{tenantId}/
  // {driftId}/audit-history`); DSC keeps the existing call.
  const {
    data: auditHistory,
    isLoading: isAuditLoading,
  } = useQuery({
    queryKey: ['drift-audit-history', driftId],
    queryFn: () => (driftId ? api.getDriftAuditHistory(driftId) : null),
    enabled: !!driftId && !isTcm,
  })

  const {
    data: tcmAuditHistory,
    isLoading: isTcmAuditLoading,
  } = useQuery({
    queryKey: ['tcm-drift-audit-history', tenantId, driftId],
    queryFn: () =>
      driftId && tenantId
        ? tcmApi.getTcmDriftAuditHistory(tenantId, driftId)
        : Promise.resolve(null),
    enabled: !!driftId && isTcm && !!tenantId,
  })

  // TCM approval-impact pre-flight — mirrors the DSC impact fetch above, but
  // for the continuous-watch source. Fetched only when the detail is open on
  // an *Active* TCM row (an accept is only offered then). Unlike DSC's
  // endpoint, this preview carries NO sibling-drift cascade — it returns the
  // single drift's own resource type plus the version an accept forks from.
  // `deriveWatchAcceptImpact` distils it into honest copy (see the helper);
  // we never fabricate a "covers N more changes" count.
  const tcmIsActive = (tcmDrift?.state || '').toLowerCase() === 'active'
  const { data: tcmApprovalImpact } = useQuery<TcmDriftApprovalImpact | null>({
    queryKey: ['tcm-approval-impact', tenantId, driftId],
    queryFn: async () => {
      if (!driftId || !tenantId) return null
      try {
        return await tcmApi.getTcmDriftApprovalImpact(tenantId, driftId)
      } catch (err) {
        // Best-effort preview — a failure here must not block the accept.
        // The decision copy still carries the re-baseline warning.
        console.error('Failed to check TCM approval impact:', err)
        return null
      }
    },
    enabled: !!driftId && isTcm && !!tenantId && tcmIsActive,
  })

  const handleAction = (type: 'approve' | 'reject') => {
    // Approvals that would cascade into resource-level updates show the impact
    // notice first; the user then either backs out or proceeds to the comment
    // modal via "Accept anyway".
    if (type === 'approve' && approvalImpact?.willTriggerResourceLevelUpdate && !showImpactWarning) {
      setShowImpactWarning(true)
      setActionType(type)
      return
    }

    setActionType(type)
    setShowCommentModal(true)
  }

  const handleCommentConfirm = (comment: string) => {
    if (!actionType || !driftId) return

    const trimmed = comment.trim() || undefined
    if (actionType === 'approve') {
      onApprove(driftId, trimmed)
    } else if (actionType === 'reject') {
      onReject(driftId, trimmed)
    }

    setShowCommentModal(false)
    setActionType(null)
    setShowImpactWarning(false)
  }

  const closeCommentModal = () => {
    setShowCommentModal(false)
    setActionType(null)
  }

  if (isTcm) {
    if (tcmDriftLoading) return <DetailSkeleton />
    if (tcmDriftError || !tcmDrift) return <DetailError />
    return (
      <TcmDriftBody
        drift={tcmDrift}
        auditHistory={tcmAuditHistory?.events ?? []}
        isAuditLoading={isTcmAuditLoading}
        acceptImpact={deriveWatchAcceptImpact(tcmApprovalImpact)}
        watchLine={watchLine}
        onApprove={onApprove}
        onReject={onReject}
        isUpdating={isUpdating}
        readOnly={readOnly}
        layout={layout}
      />
    )
  }

  if (isLoading) return <DetailSkeleton />
  if (error || !drift) return <DetailError />

  const isOpenDecision = drift.status !== 'Rejected' && drift.status !== 'Remediated'
  const canApprove =
    !readOnly && (drift.status === 'PendingApproval' || drift.status === 'Pending' || drift.status === 'New')
  const canReject = !readOnly && isOpenDecision
  // Revert only while the drift is genuinely open (New, AwaitingRemediation).
  // An Approved row (accepted, or staged in Waiting to apply) is a change the
  // user chose to keep, so reverting the tenant would raise fresh drift.
  const canRevert = drift.status === 'New' || drift.status === 'AwaitingRemediation'

  // New-setting (upgrade-introduced) framing for DSC SharePoint/OneDrive rows
  // whose baseline never carried this property. Derived from the already-
  // fetched drift — no extra wiring. Null for every other row.
  const dscUpgradeCopy = isUpgradeIntroducedRow({ source: 'DSC', component: drift.component, oldValue: drift.oldValue })
    ? upgradeIntroducedCopy()
    : null

  // DSC audit trail seeded with the real detection timestamp so the section
  // is never empty (see lib/audit-timeline.ts). The endpoint returns it oldest
  // first; the timeline reads newest first, like the continuous watch's.
  const dscTimeline = [...buildDscTimeline(drift, auditHistory)].reverse()
  const revert = adminCenterFor(drift.component)

  const whatChanged = (
    <>
      <RowDetailSection title="What changed">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <SevPill severity={drift.severity} />
          <StatusPill status={drift.status} />
          {dscUpgradeCopy ? <Tag kind="outline">{dscUpgradeCopy.kicker}</Tag> : null}
        </div>
        {/* The raw M365DSC key, for the admin: the row reads it as words. */}
        <p className="m-0 mb-2 font-mono text-helper text-text-helper [overflow-wrap:anywhere]">
          {drift.component}.{drift.setting}
        </p>
        <DiffList
          rows={[
            { label: 'Your baseline', value: <ValueView value={drift.oldValue} /> },
            { label: 'Now', value: <ValueView value={drift.newValue} now /> },
          ]}
        />
        {dscUpgradeCopy ? <p className="m-0 mt-3">{dscUpgradeCopy.lead}</p> : null}
        <Provenance source="DSC" />
      </RowDetailSection>
      {drift.componentName && drift.componentName !== drift.component ? (
        <RowDetailSection title="Resource">
          <p className="m-0 [overflow-wrap:anywhere]">{drift.componentName}</p>
        </RowDetailSection>
      ) : null}
    </>
  )

  const hasRead = !!(drift.humanReadableDescription || drift.recommendedAction || triageSuggestionCopy(drift))
  const guardiansRead = (
    <>
      {hasRead ? (
        <GuardiansRead>
          <TriageReadLine fields={drift} />
          {drift.humanReadableDescription ? <p className="m-0">{drift.humanReadableDescription}</p> : null}
          {drift.recommendedAction ? (
            <p className="m-0">
              <b className="font-semibold text-text-primary">What to do. </b>
              {drift.recommendedAction}
            </p>
          ) : null}
        </GuardiansRead>
      ) : null}

      {typeof drift.riskScore === 'number' ? <RiskLine score={drift.riskScore} className={hasRead ? 'mt-4' : undefined} /> : null}

      {showImpactWarning && approvalImpact?.willTriggerResourceLevelUpdate ? (
        <InlineNotification kind="info" title="Bigger than it looks." className="mt-4">
          <ImpactBody approvalImpact={approvalImpact} />
          <span className="mt-3 flex flex-wrap gap-2">
            <Button
              kind="secondary"
              size="sm"
              onClick={() => {
                setShowImpactWarning(false)
                setActionType('approve')
                setShowCommentModal(true)
              }}
              disabled={isUpdating}
            >
              Accept anyway
            </Button>
            <Button
              kind="tertiary"
              size="sm"
              onClick={() => {
                setShowImpactWarning(false)
                setActionType(null)
              }}
            >
              Cancel
            </Button>
          </span>
        </InlineNotification>
      ) : (
        <div className="mt-4">
          {canRevert || canApprove ? (
            <ButtonSet>
              {canRevert ? (
                <ButtonLink href={revert.href} kind="primary" size="md" icon={<IconExternal />}>
                  {revert.label}
                </ButtonLink>
              ) : null}
              {canApprove ? (
                <Button kind="secondary" size="md" onClick={() => handleAction('approve')} disabled={isUpdating}>
                  Accept as new baseline
                  {NOTE_SUFFIX}
                </Button>
              ) : null}
            </ButtonSet>
          ) : null}
          {canApprove ? (
            <p className="m-0 mt-3 text-helper text-text-helper">
              {dscUpgradeCopy
                ? dscUpgradeCopy.acceptNote
                : isLongValue(drift.newValue) || !drift.newValue
                  ? 'Accepting makes the current value your baseline value, and Guardian stops flagging it.'
                  : `Accepting makes "${drift.newValue}" your baseline value, and Guardian stops flagging it.`}
            </p>
          ) : null}
          {canReject ? (
            <>
              <Button kind="ghost" size="sm" className="-ml-3 mt-2" onClick={() => handleAction('reject')} disabled={isUpdating}>
                Reject
                {NOTE_SUFFIX}
              </Button>
              <RejectConsequenceNote source="DSC" />
            </>
          ) : null}
          {readOnly && isOpenDecision ? <ReadOnlyDecisionNote /> : null}
        </div>
      )}
    </>
  )

  const history = (
    <RowDetailSection title="History">
      {isAuditLoading ? (
        <Skeleton lines={2} label="Loading the history" />
      ) : (
        <Timeline items={dscTimeline.map(dscTimelineItem)} />
      )}
    </RowDetailSection>
  )

  return (
    <>
      <DetailLayout layout={layout} columns={[whatChanged, guardiansRead, history]} />

      {/* Accept / Reject comment dialog. Reuses the same modal as the bulk
          flow so single-drift and batch actions feel identical. Reject requires
          a comment for the audit trail; accept is optional. */}
      <BulkActionCommentModal
        isOpen={showCommentModal && actionType !== null}
        onClose={closeCommentModal}
        onConfirm={handleCommentConfirm}
        actionStatus={actionType === 'reject' ? DriftStatus.Rejected : DriftStatus.Approved}
        selectedCount={1}
        isProcessing={isUpdating}
        requireComment={actionType === 'reject'}
      />
    </>
  )
}

/* ────────────────────────────────────────────────────────────────────
   Layout and small atoms
   ──────────────────────────────────────────────────────────────────── */

function DetailLayout({ layout, columns }: { layout: 'row' | 'panel'; columns: [ReactNode, ReactNode, ReactNode] }) {
  if (layout === 'row') return <RowDetail columns={columns} />
  return (
    <div className="flex flex-col gap-8">
      {columns.map((column, i) => (
        <div key={i} className="min-w-0">
          {column}
        </div>
      ))}
    </div>
  )
}

function DetailSkeleton() {
  return (
    <div className="grid gap-8 py-2 c-lg:grid-cols-3">
      <Skeleton lines={3} label="Loading the drift" />
      <Skeleton lines={4} />
      <Skeleton lines={3} />
    </div>
  )
}

function DetailError() {
  return (
    <InlineNotification kind="error" title="Couldn't load this drift.">
      Try again in a moment.
    </InlineNotification>
  )
}

/**
 * TriageReadLine — Guardian's advisory read of this drift, one line inside
 * "Guardian's read". Renders nothing when the row carries no surfaceable
 * suggestion (RealChange / un-triaged), so the block only grows a line when
 * Guardian actually has an opinion. The copy (read + reason + provenance) is
 * composed in `drift-decision-copy`.
 */
function TriageReadLine({ fields }: { fields: DriftTriageFields }) {
  const copy = triageSuggestionCopy(fields)
  if (!copy) return null
  return <p className="m-0">{copy.drawerLine}</p>
}

/** Provenance — how Guardian saw this drift, as a helper line led by the iuvo
 *  mark. Source-aware copy is owned by the pure `provenanceLabel` helper
 *  (customer words only — no collector names). `opts` carries the watch row's
 *  pipeline dimension so a detector / removal row gets its between-checks
 *  sentence instead of the generic continuous-watch one. */
function Provenance({
  source,
  opts,
}: {
  source: 'DSC' | 'TCM'
  opts?: { pipelineSource?: DriftPipelineSource | null; driftType?: string | null }
}) {
  return (
    <p className="m-0 mt-3 flex items-center gap-2 text-helper text-text-helper">
      <IuvoMark size={14} />
      <span className="min-w-0">{provenanceLabel(source, opts)}</span>
    </p>
  )
}

/** RejectConsequenceNote — one-sentence truth about what rejecting does on
 *  this source, shown beside the reject action so the decision isn't a
 *  surprise. Copy is owned by the pure `rejectConsequenceCopy` helper
 *  (verified server behaviour, customer words). `opts` carries the watch
 *  row's pipeline dimension so a removal (retire) row gets the honest
 *  retire-aware copy. */
function RejectConsequenceNote({
  source,
  opts,
}: {
  source: 'DSC' | 'TCM'
  opts?: { pipelineSource?: DriftPipelineSource | null; driftType?: string | null }
}) {
  return <p className="m-0 mt-1 max-w-[56ch] text-helper text-text-helper">{rejectConsequenceCopy(source, opts)}</p>
}

/** The risk score as a severity shape and a 0–100 figure. */
function RiskLine({ score, className }: { score: number; className?: string }) {
  const pct = Math.round(score * 100)
  return (
    <p className={`m-0 flex flex-wrap items-center gap-3 ${className ?? ''}`}>
      <span className="font-semibold text-text-primary">Risk</span>
      <SeverityGlyph level={riskLevel(score)} />
      <span className="tabular-nums text-text-secondary">{pct} of 100</span>
    </p>
  )
}

/** One DSC audit entry as a timeline event: what happened, who, and any note. */
function dscTimelineItem(entry: AuditEntry): TimelineItem {
  return {
    id: entry.id,
    current: entry.eventType === 'Detected',
    dateTime: entry.eventTimestamp,
    time: formatDriftTimestamp(entry.eventTimestamp),
    label: (
      <>
        {auditEventLabel(entry.eventType)}
        <span className="block text-helper text-text-helper">
          {friendlyActor(entry.triggeredBy, entry.triggeredByDisplayName)}
          {entry.remediationSource
            ? ` · ${REMEDIATION_LABEL[entry.remediationSource] ?? entry.remediationSource}`
            : ''}
        </span>
        {entry.comments ? (
          <span className="mt-1 block text-helper text-text-secondary">&ldquo;{entry.comments}&rdquo;</span>
        ) : null}
      </>
    ),
  }
}

/** One continuous-watch audit entry as a timeline event. */
function tcmTimelineItem(event: TcmDriftAuditEntry): TimelineItem {
  return {
    id: event.id,
    current: event.eventType === 'Detected',
    dateTime: event.eventTimestamp,
    time: formatDriftTimestamp(event.eventTimestamp),
    label: (
      <>
        {auditEventLabel(event.eventType)}
        <span className="block text-helper text-text-helper">
          {friendlyActor(event.triggeredBy, event.triggeredByDisplayName)}
        </span>
        {event.comments ? (
          <span className="mt-1 block text-helper text-text-secondary">&ldquo;{event.comments}&rdquo;</span>
        ) : null}
      </>
    ),
  }
}

/** ImpactBody — body of the resource-level impact notice. Spelled out so the
 *  notice's voice isn't clobbered by raw API copy. Spans only: it sits
 *  inside the notification's paragraph. */
function ImpactBody({ approvalImpact }: { approvalImpact: ApprovalImpact }) {
  const impacted: ImpactedDrift[] = approvalImpact.impactedDrifts ?? []
  return (
    <span className="block">
      <span className="block">
        Approving this change triggers a resource-level update.
        {impacted.length > 0 && (
          <>
            {' '}
            {impacted.length} additional drift
            {impacted.length === 1 ? '' : 's'} will be included in the same
            commit.
          </>
        )}
      </span>
      {approvalImpact.reason ? <span className="mt-2 block">{approvalImpact.reason}</span> : null}
      {impacted.length > 0 && (
        <span className="mt-3 block">
          <span className="block font-semibold text-text-primary">Also landing</span>
          <span className="mt-2 flex flex-col gap-1">
            {impacted.map((d) => (
              <span key={d.id} className="flex flex-wrap items-center gap-2">
                <span className="text-text-primary">{d.setting}</span>
                <SevPill severity={d.severity} />
              </span>
            ))}
          </span>
        </span>
      )}
      {approvalImpact.recommendation ? <span className="mt-2 block">{approvalImpact.recommendation}</span> : null}
    </span>
  )
}

/* ────────────────────────────────────────────────────────────────────
   TCM body — a TcmDriftDetail. Customer approve / reject from the Hybrid
   queue (Item 1b); the /tcm-lab Drift tab stays the operator's surface.
   Voice rules apply — named subjects on the property diff list, no
   fabricated metrics.
   ──────────────────────────────────────────────────────────────────── */

function TcmDriftBody({
  drift,
  auditHistory,
  isAuditLoading,
  acceptImpact,
  watchLine,
  onApprove,
  onReject,
  isUpdating,
  readOnly,
  layout,
}: {
  drift: TcmDriftDetailWithSource
  auditHistory: TcmDriftAuditEntry[]
  isAuditLoading: boolean
  // What accepting does to the size of Continuous watch (lib/watch-budget);
  // null when the size doesn't change or isn't known.
  watchLine: string | null
  // Distilled approval-impact preview for the continuous-watch source. The
  // parent fetches it (Active rows only) and hands down the derived shape;
  // the decision copy uses `summary` to name the baseline version the
  // accept forks from when the preview gave us a real one. No sibling-drift
  // cascade exists on this source, so there's nothing else to surface.
  acceptImpact: ReturnType<typeof deriveWatchAcceptImpact>
  // Item 1b — customer approve/reject from the Hybrid queue. The Hybrid tab
  // wires these to tcmApi.approve/rejectTcmDrift. We only render the CTAs
  // when the drift is Active, matching the Lab's gating.
  onApprove: (id: string, comment?: string) => void
  onReject: (id: string, comment?: string) => void
  isUpdating: boolean
  readOnly: boolean
  layout: 'row' | 'panel'
}) {
  const resourceName =
    drift.resourceDisplayName?.trim() || drift.resourceInstanceIdentifier || drift.resourceType

  // Lifecycle gating — Active drifts get the Accept/Reject CTAs; everything
  // else (Accepted / Remediated / Waived) shows the verdict echo instead so
  // the detail never offers an action the writer would no-op on. Mirrors
  // TcmDriftTable's `isActive` gate.
  const isActive = (drift.state || '').toLowerCase() === 'active'

  const tcmTimeline = buildTcmTimeline(drift, auditHistory)

  // "Changed by …" line from the Entra audit-log lookup; null unless it found one.
  const actor = actorLineCopy(drift)

  // Pipeline dimension for the provenance + reject-consequence copy. The detail
  // row already carries the wire `Source` ('Monitor' | 'CreationDetector' |
  // 'DeletionDetector' | 'SnapshotDiff'); thread it + driftType through the
  // shared opts arg so the deletion-detector / creation-detector branches in
  // the pure copy helpers fire here exactly as they do in the queue. For an
  // ordinary Monitor row this is a no-op — the helpers fall through to the
  // existing generic continuous-watch copy.
  const watchCopyOpts = {
    pipelineSource: drift.source ?? null,
    driftType: drift.driftType,
  }

  // A retire-eligible removal (a watched policy that vanished). Gated on the
  // SOURCE via the same predicate the queue uses, never on driftType alone, so
  // a Monitor `Removed` row keeps the generic Accept framing. Drives the Accept
  // CTA relabel + the retire-aware decision copy below.
  const isRetireRow = isDeletionDetectorRow({ source: 'TCM', ...watchCopyOpts })
  // The queue's retire copy (label + honest accept/reject body). Reused here so
  // the detail and the queue read identically for a removal row. Null for every
  // other row, so the decision keeps its existing re-baseline framing.
  const retireCopy = isRetireRow
    ? deletionDetectorCopy(drift.resourceDisplayName?.trim() || null)
    : null

  // An acknowledgeable row (a SnapshotDiff Added/Modified finding). The server
  // accepts these through the same stage → apply flow as any other row, with
  // no baseline write — acknowledging just records that the change was
  // reviewed. Gated on SOURCE, mirroring isDeletionDetectorRow/
  // isCreationDetectorRow, so this never collides with the
  // SnapshotDiff+Removed retire-eligible leg (isDeletionDetectorRow already
  // claims that one). Relabels the Accept CTA and swaps in honest copy about
  // what acknowledging does.
  const isAcknowledge = isAcknowledgeRow({ source: 'TCM', ...watchCopyOpts })
  const ackCopy = isAcknowledge ? acknowledgeCopy() : null

  // Comment modal intent — `null` is closed. Both flows route their comment
  // up through onApprove / onReject; the parent owns the mutation + toast.
  const [commentAction, setCommentAction] = useState<'approve' | 'reject' | null>(null)

  const handleConfirm = (comment: string) => {
    const trimmed = comment.trim() || undefined
    if (commentAction === 'approve') {
      onApprove(drift.id, trimmed)
    } else if (commentAction === 'reject') {
      onReject(drift.id, trimmed)
    }
    // The parent refreshes the queue on success; close the modal optimistically
    // either way so a re-open starts clean. If the mutation errors the parent
    // surfaces a toast and the drift stays Active for a retry.
    setCommentAction(null)
  }

  // A SnapshotDiff (Tier-2 full-capture) row diffs against the previous photo,
  // not a watched baseline, so "Expected" / "Actual" would misstate what
  // expectedValue holds. "Before" / "After" reads correctly for a
  // photo-to-photo diff (not "Now": a still-open row's values can predate the
  // latest sweep). Every other pipeline keeps the baseline-vs-live framing
  // (Adam, 2026-09-24).
  const isSweep = drift.source === 'SnapshotDiff'
  const severity = (drift.severity ?? 'Low') as import('@/types').DriftSeverity
  const revert = adminCenterFor(drift.resourceType)
  const acceptLabel = retireCopy ? retireCopy.acceptLabel : ackCopy ? ackCopy.acceptLabel : 'Accept as new baseline'

  const whatChanged = (
    <>
      <RowDetailSection title="What changed">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {/* SevPill expects DriftSeverity enum values; TCM strings overlap
              ('Critical' | 'High' | 'Medium' | 'Low') so passing through
              works for the common case; nulls read as Low. */}
          <SevPill severity={severity} />
          {/* Customer words only on this surface (Monitoring UX plan D5) —
              the tag names the tier, not the internal collector. Tier-1
              (the 6h monitor) reads "Continuous watch"; Tier-2 (the
              recurring config-sweep, `source === 'SnapshotDiff'`) reads
              "Full capture" (Adam, 2026-09-23; terminology-decisions-2026-09-22.md). */}
          <Tag kind="outline">{isSweep ? 'Full capture' : 'Continuous watch'}</Tag>
        </div>
        <p className="m-0 mb-3">
          <b>{formatTcmResourceType(drift.resourceType)}</b>, {drift.driftType.toLowerCase()}.{' '}
          <span className="[overflow-wrap:anywhere]">{resourceName}</span>
        </p>
        {actor ? (
          <p className="m-0 mb-3" title={actor.hover ?? undefined}>{actor.text}</p>
        ) : null}
        {drift.driftedProperties.length === 0 ? (
          <p className="m-0">No drifted properties recorded for this row.</p>
        ) : (
          <div className="flex flex-col gap-4">
            {drift.driftedProperties.map((p, idx) => (
              <div key={`${p.propertyPath}-${idx}`} className="min-w-0">
                <p className="m-0 mb-2 font-mono text-helper text-text-helper [overflow-wrap:anywhere]">
                  {p.propertyPath || 'unnamed property'}
                </p>
                <DriftValueDiff
                  expected={p.expectedValue}
                  actual={p.actualValue}
                  full={
                    <DiffList
                      rows={[
                        { label: isSweep ? 'Before' : 'Expected', value: <ValueView value={formatTcmValue(p.expectedValue)} /> },
                        { label: isSweep ? 'After' : 'Actual', value: <ValueView value={formatTcmValue(p.actualValue)} now /> },
                      ]}
                    />
                  }
                />
              </div>
            ))}
          </div>
        )}
        <Provenance source="TCM" opts={watchCopyOpts} />
      </RowDetailSection>
    </>
  )

  const hasRead = !!(
    drift.humanReadableDescription ||
    drift.recommendedAction ||
    drift.businessImpact ||
    triageSuggestionCopy(drift)
  )
  // TCM stores risk as a 0–100 int; the risk line expects 0–1. Scale down.
  const risk = typeof drift.riskScore === 'number' ? (drift.riskScore > 1 ? drift.riskScore / 100 : drift.riskScore) : null

  // Decision copy. A retire row gets the queue's honest retire body
  // (accepting retires the gone policy OUT of the watched set); an
  // acknowledge row (#95) gets the honest acknowledge body (accepting
  // records the review with no baseline write); every other row keeps
  // the re-baseline warning — accepting lands a new baseline version
  // via the writer, stated plainly so the user knows the accept
  // changes what "normal" means going forward.
  const decisionCopy = retireCopy
    ? retireCopy.body
    : ackCopy
      ? ackCopy.body
      : acceptImpact.summary
        ? `Accepting makes this the new normal. ${acceptImpact.summary} Rejecting keeps the baseline as-is and records why.`
        : 'Accepting makes this the new normal. Guardian forks a fresh baseline version, and this change stops reading as drift. Rejecting keeps the baseline as-is and records why.'

  const guardiansRead = (
    <>
      {hasRead ? (
        <GuardiansRead>
          <TriageReadLine fields={drift} />
          {drift.humanReadableDescription ? <p className="m-0">{drift.humanReadableDescription}</p> : null}
          {drift.businessImpact ? (
            <p className="m-0">
              <b className="font-semibold text-text-primary">Business impact. </b>
              {drift.businessImpact}
            </p>
          ) : null}
          {drift.recommendedAction ? (
            <p className="m-0">
              <b className="font-semibold text-text-primary">What to do. </b>
              {drift.recommendedAction}
            </p>
          ) : null}
        </GuardiansRead>
      ) : null}

      {risk !== null ? <RiskLine score={risk} className={hasRead ? 'mt-4' : undefined} /> : null}

      <div className="mt-4">
        {isActive && readOnly ? (
          <ReadOnlyDecisionNote />
        ) : isActive ? (
          <>
            {/* Mockup 02's order: the decision buttons, then what accepting does as a helper line. */}
            <ButtonSet>
              <ButtonLink href={revert.href} kind="primary" size="md" icon={<IconExternal />}>
                {revert.label}
              </ButtonLink>
              <Button kind="secondary" size="md" onClick={() => setCommentAction('approve')} disabled={isUpdating}>
                {acceptLabel}
                {NOTE_SUFFIX}
              </Button>
            </ButtonSet>
            <p className="m-0 mt-3 text-helper text-text-helper">{decisionCopy}</p>
            {watchLine ? <p className="m-0 mt-1 text-helper text-text-helper">{watchLine}</p> : null}
            <Button kind="ghost" size="sm" className="-ml-3 mt-2" onClick={() => setCommentAction('reject')} disabled={isUpdating}>
              Reject
              {NOTE_SUFFIX}
            </Button>
            <RejectConsequenceNote source="TCM" opts={watchCopyOpts} />
          </>
        ) : (
          <TcmVerdictEcho drift={drift} />
        )}
      </div>
    </>
  )

  // Seeded with Detected (and a monitor-closed Remediated when the ingestion
  // cycle didn't log one) — see lib/audit-timeline.ts.
  const history = (
    <RowDetailSection title="History">
      {isAuditLoading ? (
        <Skeleton lines={2} label="Loading the history" />
      ) : (
        <Timeline items={tcmTimeline.map(tcmTimelineItem)} />
      )}
    </RowDetailSection>
  )

  return (
    <>
      <DetailLayout layout={layout} columns={[whatChanged, guardiansRead, history]} />

      {/* Comment modal — reuses the Lab's TcmCommentModal so accept/reject
          feel identical across surfaces. Comment is optional for accept,
          encouraged for reject (the modal copy nudges either way). */}
      <TcmCommentModal
        isOpen={commentAction !== null}
        title={
          commentAction === 'reject'
            ? 'Reject this drift'
            : retireCopy
              ? 'Retire this removed policy'
              : ackCopy
                ? ackCopy.modalTitle
                : 'Accept this baseline change'
        }
        confirmLabel={commentAction === 'reject' ? 'Reject' : acceptLabel}
        confirmVariant={commentAction === 'reject' ? 'danger' : 'primary'}
        isSubmitting={isUpdating}
        onConfirm={handleConfirm}
        onCancel={() => setCommentAction(null)}
      />
    </>
  )
}

/**
 * ReadOnlyDecisionNote — stands in for Accept / Reject on an open drift when
 * the viewer's role is below Approver, so the decision slot says who decides
 * instead of going blank. Styled like TcmVerdictEcho.
 */
function ReadOnlyDecisionNote() {
  return (
    <RowDetailSection title="Decision" as="h4" className="mt-4">
      <p className="m-0 font-semibold text-text-primary">Still open.</p>
      <p className="m-0 mt-1">
        Accepting or rejecting a drift takes the Approver role. Ask an Approver or admin on your team to decide this one.
      </p>
    </RowDetailSection>
  )
}

/**
 * TcmVerdictEcho — for non-Active TCM drifts, name the verdict that's
 * already on record instead of offering a dead action. Uses only the
 * fields the detail DTO carries today (`acceptedAt`/`acceptedBy`,
 * `remediatedAt`, `state`, `source`, `driftType`); no fabricated attribution.
 */
function TcmVerdictEcho({ drift }: { drift: TcmDriftDetailWithSource }) {
  const state = (drift.state || '').toLowerCase()

  let headline = 'Settled.'
  let detail: string | null = null

  if (state === 'accepted') {
    // An acknowledge row (#95) settles to Accepted with no baseline write —
    // the echo must never claim otherwise, so it gets its own honest verdict
    // instead of the "into the baseline" phrasing every other accepted row
    // earns.
    const acknowledged = isAcknowledgeRow({
      source: 'TCM',
      pipelineSource: drift.source ?? null,
      driftType: drift.driftType,
    })
    headline = acknowledged ? 'Acknowledged as the new normal. Not added to Continuous watch.' : 'Accepted into the baseline.'
    if (drift.acceptedAt) {
      const when = formatDriftTimestamp(drift.acceptedAt).split(' · ')[0]
      detail = drift.acceptedBy
        ? `${drift.acceptedBy} ${acknowledged ? 'acknowledged' : 'accepted'} this on ${when}.`
        : `${acknowledged ? 'Acknowledged' : 'Accepted'} on ${when}.`
    }
  } else if (state === 'remediated') {
    headline = 'Remediated.'
    if (drift.remediatedAt) {
      detail = `Resolved on ${formatDriftTimestamp(drift.remediatedAt).split(' · ')[0]}.`
    }
  } else if (state === 'waived') {
    headline = 'Waived.'
    detail = 'Set aside for now. No baseline change.'
  }

  return (
    <RowDetailSection title="Where this landed" as="h4">
      <p className="m-0 font-semibold text-text-primary">{headline}</p>
      {detail ? <p className="m-0 mt-1">{detail}</p> : null}
    </RowDetailSection>
  )
}

/**
 * formatTcmValue — TCM property values can be primitives, objects, arrays,
 * or null. We JSON-stringify objects/arrays and pretty-print so the value
 * view renders meaningfully without `[object Object]`.
 */
function formatTcmValue(v: unknown): string {
  if (v === null || v === undefined) return '(none)'
  if (typeof v === 'string') return v
  if (typeof v === 'number' || typeof v === 'boolean') return String(v)
  try {
    return JSON.stringify(v, null, 2)
  } catch {
    return String(v)
  }
}
